import { AfterViewInit, ChangeDetectorRef, Component, Inject, OnInit, ViewChild } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialog } from '@angular/material/dialog';
import { AppService, DentalReferralFormReviewNote, DentalReferralForm, HRATReferral, MaternityReferral, PCPReferral, SaveReferralReviewNoteRequest, DeleteReferralReviewNoteRequest, MemberSearchParameters, UpdateReferralMemberAssignmentRequest, DentalReferralFormReview } from '../../app.service';
import { AlertService } from '../../common/alert.service';
import { LoadingService } from '../../common/loading.service';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { catchError, finalize, map, Observable, of } from 'rxjs';
import { NotesContainerComponent } from '../../common/notes-container/notes-container.component';
import { ReprocessRequestDialogComponent } from '../../common/reprocess-request-dialog/reprocess-request-dialog.component';
import { MemberSearchDialogComponent } from '../../common/member-search-dialog/member-search-dialog.component';
import { ReferralHistoryComponent } from '../../common/referral-history/referral-history.component';
import { AuthService } from '../../auth/services/auth.service';
import { DatePipe } from '@angular/common';

@Component({
  selector: 'app-dco-referral-details-dialog',
  templateUrl: './dco-referral-details-dialog.component.html',
  styleUrl: './dco-referral-details-dialog.component.scss'
})
export class DcoReferralDetailsDialogComponent implements OnInit, AfterViewInit {

  @ViewChild('ReferralNotesContainer')
  public ReferralNotesContainer: NotesContainerComponent;

  @ViewChild('ReferralHistoryContainer')
  public ReferralHistoryContainer: ReferralHistoryComponent;
 
  openedReferral: DentalReferralForm = null;
  notesForm: FormGroup;

  showForm: boolean = false;

  showMemberEnrollmentHistory: boolean = false;
  showMemberReferralHistory: boolean = false;
  allowMemberUpdates: boolean = false;

  isReadOnly: boolean = false;

  showReferralActivitySection: boolean = false;

  memberReferralCount = 0;

  refreshParentOnClose: boolean = false;


  constructor(
    private service: AppService,
    private alert: AlertService,
    private spinner: LoadingService,
    private dialog: MatDialogRef<DcoReferralDetailsDialogComponent>,
    private reprocessDialog: MatDialog,
    private cdr: ChangeDetectorRef, // Inject ChangeDetectorRef
    private authService: AuthService,
    private datePipe: DatePipe,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) { }

  ngOnInit(): void {
    this.openedReferral = this.data.referral;
    this.showMemberEnrollmentHistory = this.data.showMemberEnrollmentHistory;
    this.showReferralActivitySection = this.data.showReferralActivitySection;
    this.showMemberReferralHistory = this.data.showMemberReferralHistory;
    this.allowMemberUpdates = this.data.allowMemberUpdates;

    if (this.openedReferral) {
      this.loadNotes();

      const lockedByUserId = this.openedReferral?.review?.lockedByUserId;
      const currentUserId = this.authService.getDecodedToken()?.userId;

      if (this.openedReferral.review?.isLocked && lockedByUserId?.toLowerCase() !== currentUserId?.toLowerCase()) {
        this.isReadOnly = true;
      }
    }

    this.notesForm = new FormGroup({
      notes: new FormControl('', [Validators.required, Validators.maxLength(500)]),
    });
  }


  ngAfterViewInit(): void {
    // Ensure `ReferralNotesContainer` is available before calling methods on it
    if (this.openedReferral) {
      this.loadNotes();
    }
  }
  
  onClose(): void {
    // Close the dialog without saving changes
    this.dialog.close({ data: { refresh: this.refreshParentOnClose } });
  }

  isHRATReferral(referral: DentalReferralForm): referral is HRATReferral {
    return referral?.referralType === 'HRAT';
  }

  isMaternityReferral(referral: DentalReferralForm): referral is MaternityReferral {
    return referral?.referralType === 'Maternity';
  }

  isPCPReferral(referral: DentalReferralForm): referral is PCPReferral {
    return referral?.referralType === 'PCP';
  }

  public loadNotes(): void {
    if (this.ReferralNotesContainer) {
      this.ReferralNotesContainer.clearNewNote();
      this.ReferralNotesContainer.notes = this.openedReferral?.review?.notes;
      
      this.ReferralNotesContainer.showNewNote = !(this.ReferralNotesContainer.notes && this.ReferralNotesContainer.notes.length > 0);

      // Run change detection to avoid ExpressionChangedAfterItHasBeenCheckedError
      this.cdr.detectChanges();
    } 
  }


  public SaveNewNote(newNote: DentalReferralFormReviewNote) {
    if (!this.openedReferral.review.id) {
      newNote.reviewId = 0;
    } else {
      newNote.reviewId = this.openedReferral.review.id;
    }
    this.SaveNote(newNote);
  }

  public SaveNote(noteToSave: DentalReferralFormReviewNote) {
    console.log(noteToSave);

    //openedReferral
    if (noteToSave) {
      const request = new SaveReferralReviewNoteRequest();
      request.note = noteToSave;
      request.referralId = this.openedReferral.id;
      request.referralType = this.openedReferral.referralType;
      //console.log(`[DcoReferralDetailsDialogComponent - SaveNote] - request: `, request);

      this.service.SaveReferralReviewNote(request).subscribe(
        response => {
          if (response.notes) {
            this.openedReferral.review.notes = response.notes;
            this.loadNotes();
            if (this.ReferralHistoryContainer) {
              this.ReferralHistoryContainer.refresh();
            } else {
              console.error('ReferralHistoryContainer is not available');
            }
          }
          this.alert.success('Note Saved');
        },
        error => {
          this.alert.errorWithDetails(error, 'Unable to save note.');
        });

    }

  }

  public DeleteNote(noteToDelete: DentalReferralFormReviewNote) {
    if (window.confirm('Are you sure you want to delete this note?')) {
      if (noteToDelete) {
        const request = new DeleteReferralReviewNoteRequest();
        request.noteId = noteToDelete.id;

        this.service.DeleteReferralReviewNote(request).subscribe(
          response => {
            this.openedReferral.review.notes = response.notes;
            this.loadNotes();
            if (this.ReferralHistoryContainer) {
              this.ReferralHistoryContainer.refresh();
            } else {
              console.error('ReferralHistoryContainer is not available');
            }
            this.alert.success('Note Deleted');
          },
          error => {
            this.alert.errorWithDetails(error, 'Unable to delete task note.');
          });
      }
    }
  }

  openReprocessReferralDialog(referral: DentalReferralForm) {
    if (this.isReadOnly) {
      const lockedBy = referral.review?.lockedByUser?.fullName;
      const formattedDate = this.datePipe.transform(referral.review?.lockedDate, 'MM/dd/yyyy h:mm a');
      this.alert.warning(`This referral cannot be sent for reprocessing because it is currently locked by ${lockedBy} on ${formattedDate}.`);
      return;
    }
    const dialogRef = this.reprocessDialog.open(ReprocessRequestDialogComponent, {
      data: { referral },
      //panelClass: 'custom-dialog-panel', // Apply the custom class
      //height: '90vh', // Set height as desired
      disableClose: true, // Disable closing the dialog when clicking outside
    })
    .afterClosed()
    .subscribe(response => {
      if (response?.data != null) {
        if (response.data.refresh) {
          this.closeDialogWithRefresh();

        }
      }
    });
  }

  closeDialogWithRefresh() {
    this.dialog.close({ data: { refresh: true } });
  }

  public onMemberUpdate() {
    const searchMemberParameters = new MemberSearchParameters();
    searchMemberParameters.dmapId = this.openedReferral.memberMedicaidId;
    searchMemberParameters.dob = this.openedReferral.memberDob;
    searchMemberParameters.firstName = this.openedReferral.memberFirstName;
    searchMemberParameters.lastName = this.openedReferral.memberLastName;
    searchMemberParameters.phoneNumber = this.openedReferral.memberPhoneNumber;

    const dialogRef = this.reprocessDialog.open(MemberSearchDialogComponent, {
      data: { searchMemberParameters: searchMemberParameters },
      panelClass: 'member-search-dialog-panel',
      disableClose: true,
    });

    dialogRef.afterClosed().subscribe(response => {
      if (response?.selectedMember != null) {
        const sendToDCO = response.sendToDCO;

        console.log('sendToDCO: ', sendToDCO);

        // Show spinner before API call
        this.spinner.show();

        this.service.GetMemberData({ memberId: response.selectedMember.dmapId })
          .pipe(finalize(() => this.spinner.hide()))
          .subscribe({
            next: (res) => {
              if (res.success && res.isMemberFound) {
                console.log('response: ', res);
                // PART 1: Update mailing address & language from MemberMasterDimLite
                this.openedReferral.memberMailingAddress1 = res.memberMasterDimLite.mailingAddressLine1;
                this.openedReferral.memberMailingAddress2 = res.memberMasterDimLite.mailingAddressLine2;
                this.openedReferral.memberMailingCity = res.memberMasterDimLite.mailingCity;
                this.openedReferral.memberMailingState = res.memberMasterDimLite.mailingStateCode;
                this.openedReferral.memberMailingZip = res.memberMasterDimLite.mailingZipCode;
                this.openedReferral.memberMailingCounty = res.memberMasterDimLite.mailingCounty;
                this.openedReferral.memberLanguageSpoken = res.memberMasterDimLite.languageSpoken;
                this.openedReferral.memberLanguageWritten = res.memberMasterDimLite.languageWritten;
                this.openedReferral.cco = res.member.cco;

                // PART 2: Update rest of referral fields from selectedMember
                this.openedReferral.memberMedicaidId = response.selectedMember.dmapId;
                this.openedReferral.memberFirstName = response.selectedMember.firstName;
                this.openedReferral.memberLastName = response.selectedMember.lastName;
                this.openedReferral.memberDob = response.selectedMember.dob;
                this.openedReferral.memberPhoneNumber = response.selectedMember.phoneNumber;

                this.cdr.detectChanges(); // Force UI update

                this.UpdateReferralMemberAssignment(this.openedReferral, sendToDCO, response.manualUpdate)
                  .subscribe(success => {
                    if (success) {
                      if (sendToDCO) {
                        this.closeDialogWithRefresh();
                      } else {
                        this.ReferralHistoryContainer?.refresh();
                      }
                    } else {
                      console.log('Update failed.');
                    }
                  });

              } else {
                this.alert.error(res.message || 'Member not found.');
              }
            },
            error: (err) => {
              console.error('Error fetching member data', err);
              this.alert.error('Error fetching member data.');
            }
          });

      } else {
        console.log('No member selected. Hiding spinner.');
        this.spinner.hide();
      }
    });
  }




  private UpdateReferralMemberAssignment(referral: DentalReferralForm, sendToDCO: boolean, manualUpdate: boolean): Observable<boolean> {
    const request = new UpdateReferralMemberAssignmentRequest();
    request.referral = referral;
    request.referral.review = new DentalReferralFormReview();
    request.referral.review.referralType = referral.referralType;
    request.sendToDCO = sendToDCO;
    request.manualUpdate = manualUpdate;
    
    this.spinner.show();
    this.cdr.detectChanges(); // Force UI update

    return this.service.UpdateReferralMemberAssignment(request).pipe(
      map(response => {
        if (response.success) {
          if (sendToDCO && response.memberDCO) this.alert.success(`Member info updated and referral sent to ${response.memberDCO.displayName}.`);
          else this.alert.success('Member info updated on referral');
          return true;
        } else {
          this.alert.warning(response.message);
        }
        return false;
      }),
      catchError(error => {
        this.alert.errorWithDetails(error, 'Unable to update member.');
        return of(false);
      }),
      finalize(() => {
        this.spinner.hide();
        this.cdr.detectChanges();
      })
    );
  }

  hasMailingAddress(): boolean {
    const m = this.openedReferral;
    return !!(m?.memberMailingAddress1 || m?.memberMailingAddress2 || m?.memberMailingCity || m?.memberMailingState || m?.memberMailingZip);
  }

  formatZip(zip?: string): string {
    if (!zip) return '';
    const cleaned = zip.replace(/\D/g, '');
    if (cleaned.length === 9) {
      return `${cleaned.substring(0, 5)}-${cleaned.substring(5)}`;
    }
    return cleaned;
  }

  onChildRequestUpstreamRefresh() {
    this.refreshParentOnClose = true;
    console.log('[]refresh requested');
  }

}
